import {useEffect, useRef} from 'react';

type TabFocusCallback = () => void;

const FOCUS_TRIGGER_DEBOUNCE_MS = 200;

const useTabFocusEffect = (callback: TabFocusCallback) => {
    const callbackRef = useRef<TabFocusCallback>(callback);
    const lastTriggerTimestampRef = useRef<number>(0);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        const runCallback = () => {
            const now = Date.now();
            if (now - lastTriggerTimestampRef.current < FOCUS_TRIGGER_DEBOUNCE_MS) {
                return;
            }
            lastTriggerTimestampRef.current = now;
            callbackRef.current();
        };

        const handleWindowFocus = () => {
            runCallback();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                runCallback();
            }
        };

        window.addEventListener('focus', handleWindowFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleWindowFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);
};

export default useTabFocusEffect;